/**
 * useWidgetManager - React hook for interacting with the WidgetManager
 * 
 * This hook provides a convenient way to interact with the widget manager
 * from React components, including subscribing to state changes and
 * requesting widget operations.
 */

import { useEffect, useState } from 'react';
import { widgetManager, WidgetState, WidgetType } from '../lib/WidgetManager';

export interface UseWidgetManagerReturn {
    /**
     * Current widget state
     */
    state: WidgetState;

    /**
     * Request to open a specific widget
     * @param widgetType - The type of widget to open
     * @returns Promise that resolves to whether the request was successful
     */
    requestOpen: (widgetType: WidgetType) => Promise<boolean>;

    /**
     * Request to close a specific widget
     * @param widgetType - The type of widget to close
     * @returns Whether the request was successful
     */
    requestClose: (widgetType: WidgetType) => boolean;

    /**
     * Toggle the widget open/closed state
     * @returns The new active widget type
     */
    toggleWidget: () => WidgetType;

    /**
     * Check if a specific widget is active
     * @param widgetType - The type of widget to check
     * @returns Whether the widget is active
     */
    isActive: (widgetType: WidgetType) => boolean;

    /**
     * Check if any widget is active
     * @returns Whether any widget is active
     */
    isAnyActive: () => boolean;

    /**
     * Set floating button visibility
     * @param visible - Whether the floating button should be visible
     */
    setFloatingButtonVisibility: (visible: boolean) => void;

    /**
     * Force close all widgets
     */
    closeAll: () => void;
}

/**
 * Hook for interacting with the widget manager
 * @returns Widget manager interface
 */
export const useWidgetManager = (): UseWidgetManagerReturn => {
    const [state, setState] = useState<WidgetState>(widgetManager.getState());

    useEffect(() => {
        // Subscribe to state changes
        const unsubscribe = widgetManager.subscribe((newState) => {
            setState(newState);
        });

        // Cleanup on unmount
        return unsubscribe;
    }, []);

    return {
        state,
        requestOpen: (widgetType: WidgetType) => widgetManager.requestOpen(widgetType),
        requestClose: (widgetType: WidgetType) => widgetManager.requestClose(widgetType),
        toggleWidget: () => widgetManager.toggleWidget(),
        isActive: (widgetType: WidgetType) => state.activeWidget === widgetType,
        isAnyActive: () => state.activeWidget !== 'none',
        setFloatingButtonVisibility: (visible: boolean) =>
            widgetManager.setFloatingButtonVisibility(visible),
        closeAll: () => widgetManager.closeAll(),
    };
};
